import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft,
  UploadCloud,
  AlertCircle,
  Eye,
  PanelRightClose,
  PanelRightOpen,
  Sparkles,
} from 'lucide-react';
import {
  DEFAULT_EXTERNAL_CONFIRMATION,
  schemaHasQuestionGroup,
  trainingQuestions,
  validateExternalModule,
  validateQuestionGroupSettings,
  validateServeQuestionCount,
  validateTrainingModuleSchema,
  type TrainingModuleKind,
  type TrainingModuleSchema,
  type TrainingPageDef,
  type TrainingRetryMode,
} from '@hcc/types';
import { ApiError } from '../../../lib/api';
import {
  useAiBuilderAvailable,
  usePublishTrainingModule,
  useSaveTrainingModuleDraft,
  type AiCourseDraft,
  type TrainingModuleSummary,
} from '../../../hooks/useTrainingModules';
import { Button } from '../../ui/Button';
import { ConfirmModal } from '../../ui/ConfirmModal';
import {
  emptySchema,
  addPage,
  followUpSources,
  addPictureSlides,
  addTemplateSlide,
  addSection,
  appendPages,
  duplicatePage,
  movePage,
  remapIndexAfterMove,
  removePage,
  removeSection,
  reorderPages,
  setPageSection,
  updatePage,
  updateSection,
} from './moduleOps';
import { ModuleSettings } from './ModuleSettings';
import { NotifyAnswersCard } from './NotifyAnswersCard';
import { QuestionGroupEditor } from './QuestionGroupEditor';
import { TextGroupEditor } from './TextGroupEditor';
import { FollowUpEditor, type FollowUpSourceOption } from './FollowUpEditor';
import { ExternalModuleEditor } from './ExternalModuleEditor';
import { AiBuilderModal } from './AiBuilderModal';
import { SectionManager } from './SectionManager';
import { PreviewAsLearnerModal } from './PreviewAsLearnerModal';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { PageListSidebar } from './PageListSidebar';
import { ModulePreview } from './ModulePreview';
import { ContentPageEditor } from './ContentPageEditor';
import { ChoiceQuestionEditor } from './ChoiceQuestionEditor';
import { TrueFalseQuestionEditor } from './TrueFalseQuestionEditor';
import { ShortTextQuestionEditor } from './ShortTextQuestionEditor';
import { CategorizeQuestionEditor } from './CategorizeQuestionEditor';
import { OrderQuestionEditor } from './OrderQuestionEditor';
import { HotspotQuestionEditor } from './HotspotQuestionEditor';
import { MatchQuestionEditor } from './MatchQuestionEditor';

function latestVersion(module: TrainingModuleSummary) {
  return [...module.versions].sort((a, b) => b.version - a.version)[0] ?? null;
}

/**
 * The module builder: settings + page list + per-type editors on the left,
 * a live try-it-out preview on the right. Autosave/publish orchestration
 * mirrors the form builder (debounced draft, unmount flush, publish
 * validates and versions).
 */
export function ModuleEditor({
  module,
  onBack,
}: {
  module: TrainingModuleSummary;
  onBack: () => void;
}) {
  const seed = useMemo(() => latestVersion(module), [module]);
  const draft = module.draft;
  const [title, setTitle] = useState(draft?.title ?? seed?.title ?? module.title);
  const [description, setDescription] = useState(draft?.description ?? seed?.description ?? '');
  const [passMark, setPassMark] = useState(draft?.pass_mark ?? seed?.pass_mark ?? 80);
  const [retryMode, setRetryMode] = useState<TrainingRetryMode>(
    draft?.retry_mode ?? seed?.retry_mode ?? 'full_retake',
  );
  const [serveQuestionCount, setServeQuestionCount] = useState<number | null>(
    draft?.serve_question_count ?? seed?.serve_question_count ?? null,
  );
  const [shuffleQuestions, setShuffleQuestions] = useState<boolean>(
    draft?.shuffle_questions ?? seed?.shuffle_questions ?? false,
  );
  const [schema, setSchema] = useState<TrainingModuleSchema>(
    draft?.schema ?? seed?.schema ?? emptySchema(),
  );
  const [graded, setGraded] = useState<boolean>(draft?.graded ?? seed?.graded ?? true);
  const [kind, setKind] = useState<TrainingModuleKind>(draft?.kind ?? seed?.kind ?? 'quiz');
  const [externalUrl, setExternalUrl] = useState(draft?.external_url ?? seed?.external_url ?? '');
  const [externalInstructions, setExternalInstructions] = useState(
    draft?.external_instructions ?? seed?.external_instructions ?? '',
  );
  const [externalConfirmation, setExternalConfirmation] = useState(
    draft?.external_confirmation ?? seed?.external_confirmation ?? DEFAULT_EXTERNAL_CONFIRMATION,
  );
  const isExternal = kind === 'external';
  const questionCount = useMemo(() => trainingQuestions(schema).length, [schema]);
  const hasGroups = useMemo(() => schemaHasQuestionGroup(schema), [schema]);
  const [activePage, setActivePage] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const aiAvailable = useAiBuilderAvailable();
  // The live side-by-side preview; toggle off to give the editor full width.
  const [showLivePreview, setShowLivePreview] = useState(true);
  const active = Math.min(activePage, schema.pages.length - 1);
  const page = schema.pages[active];

  const publish = usePublishTrainingModule();
  const saveDraft = useSaveTrainingModuleDraft();

  // ---- Autosave (draft), mirroring FormEditor -----------------------------
  type SaveState = 'idle' | 'saving' | 'saved' | 'error';
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const dirtyRef = useRef(false);
  const publishedRef = useRef(false);
  const firstRun = useRef(true);
  const latest = useRef({
    title,
    description,
    graded,
    passMark,
    retryMode,
    serveQuestionCount,
    shuffleQuestions,
    schema,
    kind,
    externalUrl,
    externalInstructions,
    externalConfirmation,
  });
  latest.current = {
    title,
    description,
    graded,
    passMark,
    retryMode,
    serveQuestionCount,
    shuffleQuestions,
    schema,
    kind,
    externalUrl,
    externalInstructions,
    externalConfirmation,
  };

  async function persistDraft() {
    await saveDraft.mutateAsync({
      id: module.id,
      title: latest.current.title.trim() || module.title,
      description: latest.current.description.trim(),
      kind: latest.current.kind,
      external_url: latest.current.externalUrl.trim() || null,
      external_instructions: latest.current.externalInstructions.trim() || null,
      external_confirmation: latest.current.externalConfirmation.trim() || null,
      graded: latest.current.graded,
      pass_mark: latest.current.passMark,
      retry_mode: latest.current.retryMode,
      serve_question_count: latest.current.serveQuestionCount,
      shuffle_questions: latest.current.shuffleQuestions,
      schema: latest.current.schema,
    });
  }

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    dirtyRef.current = true;
    setSaveState('saving');
    const t = setTimeout(async () => {
      try {
        await persistDraft();
        dirtyRef.current = false;
        setSaveState('saved');
      } catch {
        setSaveState('error');
      }
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    description,
    graded,
    passMark,
    retryMode,
    serveQuestionCount,
    shuffleQuestions,
    schema,
    kind,
    externalUrl,
    externalInstructions,
    externalConfirmation,
  ]);

  useEffect(() => {
    return () => {
      if (dirtyRef.current && !publishedRef.current) {
        void persistDraft().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function flushAndBack() {
    if (dirtyRef.current && !publishedRef.current) {
      try {
        await persistDraft();
        dirtyRef.current = false;
      } catch {
        /* leave it; unmount flush will retry */
      }
    }
    onBack();
  }

  function requestPublish() {
    if (isExternal) {
      const ext = validateExternalModule({
        external_url: externalUrl.trim(),
        external_confirmation: externalConfirmation.trim(),
      });
      if (!ext.valid) {
        setErrors(ext.errors);
        toast.error('Fix the highlighted problems before publishing');
        return;
      }
      setErrors([]);
      setConfirmPublish(true);
      return;
    }
    const result = validateTrainingModuleSchema(schema, { graded });
    const serve = validateServeQuestionCount(serveQuestionCount, schema);
    const groups = validateQuestionGroupSettings(schema, {
      retry_mode: retryMode,
      serve_question_count: serveQuestionCount,
      shuffle_questions: shuffleQuestions,
    });
    if (!result.valid || !serve.valid || !groups.valid) {
      setErrors([...result.errors, ...serve.errors, ...groups.errors]);
      toast.error('Fix the highlighted problems before publishing');
      return;
    }
    setErrors([]);
    setConfirmPublish(true);
  }

  async function onPublish() {
    setConfirmPublish(false);
    try {
      const res = await publish.mutateAsync({
        id: module.id,
        title: title.trim(),
        description: description.trim() || undefined,
        ...(isExternal
          ? {
              kind: 'external' as const,
              external_url: externalUrl.trim(),
              external_instructions: externalInstructions.trim() || undefined,
              external_confirmation: externalConfirmation.trim(),
            }
          : { kind: 'quiz' as const }),
        graded,
        // An unmarked assessment has no bar to clear; every submission
        // completes it, so the stored mark is 0 rather than a stale number
        // left over from before the toggle was flipped.
        pass_mark: graded ? passMark : 0,
        retry_mode: retryMode,
        serve_question_count: serveQuestionCount,
        shuffle_questions: shuffleQuestions,
        schema,
      });
      publishedRef.current = true;
      dirtyRef.current = false;
      toast.success(`Published version ${res.version.version}`);
      onBack();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not publish');
    }
  }

  // AI builder: insert generated pages at the end (ids remapped if they clash),
  // or seed the whole module from a generated course. Both land as an editable
  // draft — the admin reviews and publishes as normal.
  function insertAiPages(pages: TrainingPageDef[]) {
    setSchema((s) => appendPages(s, pages));
    setActivePage(schema.pages.length);
  }
  function seedAiCourse(draft: AiCourseDraft) {
    if (draft.title) setTitle(draft.title);
    if (draft.description) setDescription(draft.description);
    // The model decides whether it built a test or an assessment; an
    // assessment has no answer key, so the module must match or publish fails.
    const nextGraded = draft.graded !== false;
    setGraded(nextGraded);
    if (!nextGraded) setRetryMode('full_retake');
    setPassMark(nextGraded ? draft.pass_mark : 0);
    setSchema(draft.schema);
    setActivePage(0);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<ArrowLeft className="h-4 w-4" />}
          onClick={() => void flushAndBack()}
        >
          Back to modules
        </Button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-fg-dim" aria-live="polite">
            {saveState === 'saving'
              ? 'Saving…'
              : saveState === 'saved'
                ? 'All changes saved'
                : saveState === 'error'
                  ? 'Could not save draft'
                  : 'Changes save automatically'}
          </span>
          {!isExternal ? (
            <>
              {aiAvailable.data ? (
                <Button
                  variant="secondary"
                  size="sm"
                  iconLeft={<Sparkles className="h-4 w-4" />}
                  onClick={() => setAiOpen(true)}
                >
                  Generate with AI
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                iconLeft={
                  showLivePreview ? (
                    <PanelRightClose className="h-4 w-4" />
                  ) : (
                    <PanelRightOpen className="h-4 w-4" />
                  )
                }
                aria-pressed={showLivePreview}
                onClick={() => setShowLivePreview((v) => !v)}
              >
                {showLivePreview ? 'Hide preview' : 'Live preview'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                iconLeft={<Eye className="h-4 w-4" />}
                onClick={() => setPreviewOpen(true)}
              >
                Preview as learner
              </Button>
            </>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            iconLeft={<UploadCloud className="h-4 w-4" />}
            loading={publish.isPending}
            onClick={requestPublish}
          >
            Publish version
          </Button>
        </div>
      </div>

      {errors.length > 0 ? (
        <div className="flex gap-2 rounded-token-sm border border-danger-soft/30 bg-danger-soft/15 p-3 text-sm text-danger-soft-fg">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <ul className="list-disc space-y-0.5 pl-4">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        className={`grid grid-cols-1 gap-5 ${showLivePreview && !isExternal ? 'lg:grid-cols-2' : ''}`}
      >
        <div className="space-y-4">
          <ModuleKindToggle kind={kind} onKind={setKind} />
          {isExternal ? (
            <ExternalModuleEditor
              title={title}
              description={description}
              url={externalUrl}
              instructions={externalInstructions}
              confirmation={externalConfirmation}
              onTitle={setTitle}
              onDescription={setDescription}
              onUrl={setExternalUrl}
              onInstructions={setExternalInstructions}
              onConfirmation={setExternalConfirmation}
            />
          ) : (
            <>
              <ModuleSettings
                title={title}
                description={description}
                graded={graded}
                passMark={passMark}
                retryMode={retryMode}
                serveQuestionCount={serveQuestionCount}
                shuffleQuestions={shuffleQuestions}
                questionCount={questionCount}
                hasQuestionGroups={hasGroups}
                onTitle={setTitle}
                onDescription={setDescription}
                onGraded={(next) => {
                  setGraded(next);
                  // The two settings an unmarked assessment cannot express:
                  // clear them here so the toggle never leaves the module in a
                  // state publish would reject.
                  if (!next) {
                    setRetryMode('full_retake');
                    setPassMark(0);
                  }
                }}
                onPassMark={setPassMark}
                onRetryMode={setRetryMode}
                onServeQuestionCount={setServeQuestionCount}
                onShuffleQuestions={setShuffleQuestions}
              />
              <NotifyAnswersCard module={module} />
              <SectionManager
                sections={schema.sections ?? []}
                onAdd={() => setSchema((s) => addSection(s))}
                onUpdate={(id, patch) => setSchema((s) => updateSection(s, id, patch))}
                onRemove={(id) => setSchema((s) => removeSection(s, id))}
              />
              <PageListSidebar
                schema={schema}
                active={active}
                onSelect={setActivePage}
                onMove={(i, dir) => {
                  setSchema((s) => movePage(s, i, dir));
                  if (i === active)
                    setActivePage(Math.max(0, Math.min(schema.pages.length - 1, i + dir)));
                }}
                onReorder={(from, to) => {
                  setSchema((s) => reorderPages(s, from, to));
                  // Keep the same page selected as it shifts under the drag.
                  setActivePage((a) => remapIndexAfterMove(a, from, to));
                }}
                onRemove={(i) => {
                  setSchema((s) => removePage(s, i));
                  setActivePage((a) => Math.max(0, Math.min(a, schema.pages.length - 2)));
                }}
                onAdd={(type) => {
                  setSchema((s) => addPage(s, type));
                  setActivePage(schema.pages.length);
                }}
                onAddTemplate={(template) => {
                  setSchema((s) => addTemplateSlide(s, template));
                  setActivePage(schema.pages.length);
                }}
                onAddPictures={(urls) => {
                  setSchema((s) => addPictureSlides(s, urls));
                  setActivePage(schema.pages.length);
                }}
                onDuplicate={(i) => {
                  setSchema((s) => duplicatePage(s, i));
                  setActivePage(i + 1);
                }}
                graded={graded}
              />
              {page ? (
                <div className="space-y-3 rounded-token border border-line/10 bg-surface p-3">
                  {(schema.sections?.length ?? 0) > 0 ? (
                    <label className="block text-sm">
                      <span className="font-medium text-fg-muted">Section</span>
                      <select
                        value={page.section_id ?? ''}
                        onChange={(e) =>
                          setSchema((s) => setPageSection(s, active, e.target.value || null))
                        }
                        className={SHARED_INPUT_CLASS}
                      >
                        <option value="">No section</option>
                        {(schema.sections ?? []).map((sec) => (
                          <option key={sec.id} value={sec.id}>
                            {sec.title || 'Untitled section'}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <PageEditorSwitch
                    page={page}
                    graded={graded}
                    sources={followUpSources(schema.pages, active)}
                    onChange={(next) => setSchema((s) => updatePage(s, active, next))}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        {showLivePreview && !isExternal ? (
          <div className="lg:sticky lg:top-4 lg:self-start">
            <div className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-dim">
              Try it out
            </div>
            <ModulePreview
              schema={schema}
              graded={graded}
              passMark={passMark}
              serveQuestionCount={serveQuestionCount}
              shuffleQuestions={shuffleQuestions}
              focusPageId={page?.id}
            />
          </div>
        ) : null}
      </div>

      <ConfirmModal
        open={confirmPublish}
        title="Publish a new version?"
        message={
          isExternal
            ? `Publishing creates version ${(latestVersion(module)?.version ?? 0) + 1}. Assigned learners open the link and self-certify to complete it.`
            : `Publishing creates version ${(latestVersion(module)?.version ?? 0) + 1}. New starts use it immediately; anyone mid-attempt finishes on the version they started.`
        }
        tone="warning"
        confirmLabel="Publish"
        onConfirm={() => void onPublish()}
        onClose={() => setConfirmPublish(false)}
      />

      <PreviewAsLearnerModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        schema={schema}
        passMark={passMark}
        retryMode={retryMode}
        serveQuestionCount={serveQuestionCount}
        shuffleQuestions={shuffleQuestions}
      />

      <AiBuilderModal
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        hasContent={schema.pages.length > 0}
        onInsertPages={insertAiPages}
        onSeedCourse={seedAiCourse}
      />
    </div>
  );
}

/** Choose between a graded quiz module and an external redirect module. */
function ModuleKindToggle({
  kind,
  onKind,
}: {
  kind: TrainingModuleKind;
  onKind: (next: TrainingModuleKind) => void;
}) {
  const opts: { value: TrainingModuleKind; label: string; hint: string }[] = [
    { value: 'quiz', label: 'Quiz module', hint: 'Slides and questions, graded with a pass mark' },
    {
      value: 'external',
      label: 'External link',
      hint: 'Open a link, complete it there, self-certify',
    },
  ];
  return (
    <div className="rounded-token border border-line/10 bg-surface p-3">
      <div className="text-sm font-medium text-fg-muted">Module type</div>
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {opts.map((o) => {
          const on = kind === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onKind(o.value)}
              aria-pressed={on}
              className={`rounded-token-sm border p-2.5 text-left text-sm transition ${
                on
                  ? 'border-accent bg-accent/10'
                  : 'border-line/10 bg-surface-2 hover:border-line-strong/20'
              }`}
            >
              <span className="block font-medium text-fg">{o.label}</span>
              <span className="block text-xs text-fg-dim">{o.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PageEditorSwitch({
  page,
  onChange,
  graded,
  sources,
}: {
  page: TrainingPageDef;
  onChange: (next: TrainingPageDef) => void;
  /** False hides every correct-answer and points control (an unmarked assessment). */
  graded: boolean;
  /** Choice pages before this one, for a follow-up page's source picker. */
  sources: FollowUpSourceOption[];
}) {
  switch (page.type) {
    case 'slide':
      return <ContentPageEditor slide={page} onChange={onChange} />;
    case 'question_group':
      return <QuestionGroupEditor group={page} onChange={onChange} graded={graded} />;
    case 'text_group':
      return <TextGroupEditor group={page} onChange={onChange} />;
    case 'follow_up':
      return <FollowUpEditor page={page} onChange={onChange} sources={sources} />;
    case 'single_choice':
    case 'multi_select':
      return <ChoiceQuestionEditor question={page} onChange={onChange} graded={graded} />;
    case 'true_false':
      return <TrueFalseQuestionEditor question={page} onChange={onChange} graded={graded} />;
    case 'short_text':
      return <ShortTextQuestionEditor question={page} onChange={onChange} graded={graded} />;
    case 'categorize':
      return <CategorizeQuestionEditor question={page} onChange={onChange} graded={graded} />;
    case 'ordering':
      return <OrderQuestionEditor question={page} onChange={onChange} graded={graded} />;
    case 'hotspot':
      return <HotspotQuestionEditor question={page} onChange={onChange} graded={graded} />;
    case 'matching':
      return <MatchQuestionEditor question={page} onChange={onChange} graded={graded} />;
  }
}
